/**
 * Piece/carton quantity toggle — single product page and cart page.
 *
 * Product page: the quantity input's value means different things depending
 * on the selected radio — in "szt." mode it's the real piece count, in
 * "karton" mode it's a number of cartons, converted to pieces server-side
 * (see inc/carton-packaging.php's convert_submitted_quantity()) when the
 * add-to-cart form is submitted. Switching modes here just converts the
 * on-screen number and the input's step/min so +/- moves by whole units.
 *
 * Cart page: the line is already real pieces in the session, so switching
 * instead resets it via AJAX — 1 full carton, or 1 piece — then reloads so
 * the cart totals reflect the new line.
 */

import './index.scss';

const initedToggles = new WeakSet();

/**
 * Wire up every toggle currently in the DOM. Re-run on 'updated_wc_div'
 * (dispatched by template-parts/cart/index.js's autoUpdateCart(), and by
 * WooCommerce's own core cart.js) — the cart page's toggles live inside the
 * form that auto-update replaces wholesale on every quantity change, so a
 * toggle wired up once at page load stops doing anything the moment the
 * shopper edits a quantity: the click still flips the radio visually (that's
 * just the browser), but nothing fires the AJAX call to actually persist the
 * new unit, and the next refresh shows the line back in its old unit.
 */
function initAllToggles() {
	document.querySelectorAll('[data-nitai-unit-toggle]').forEach((toggle) => {
		if (initedToggles.has(toggle)) return;

		const cartonSize = parseInt(toggle.dataset.cartonSize, 10);
		const cartItemKey = toggle.dataset.cartItemKey;

		if (!cartonSize || cartonSize < 2) return;

		initedToggles.add(toggle);

		if (cartItemKey) {
			initCartToggle(toggle, cartItemKey);
		} else {
			initProductPageToggle(toggle, cartonSize);
		}
	});
}

initAllToggles();
document.body.addEventListener('updated_wc_div', initAllToggles);

/**
 * Cart page: ask the server to reset this line to the newly selected unit,
 * then reload so quantities/totals/step everywhere reflect it.
 *
 * @param {HTMLElement} toggle       The toggle container.
 * @param {string}      cartItemKey  This line's cart item key.
 */
function initCartToggle(toggle, cartItemKey) {
	toggle.addEventListener('change', (event) => {
		// Radio name is scoped per line on the cart page (nitai_unit_{key}) —
		// see inc/carton-packaging.php's render_unit_toggle() — so match on
		// the "value" attribute instead of a fixed name.
		if (event.target.type !== 'radio') return;

		toggle.setAttribute('aria-busy', 'true');
		toggle.querySelectorAll('input').forEach((input) => {
			input.disabled = true;
		});

		const body = new URLSearchParams({
			action: 'nitai_switch_cart_unit',
			nonce: toggle.dataset.nonce,
			cart_item_key: cartItemKey,
			unit: event.target.value,
		});

		fetch(toggle.dataset.ajaxUrl, {
			method: 'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			body,
		})
			.then(() => window.location.reload())
			.catch(() => {
				toggle.removeAttribute('aria-busy');
				toggle.querySelectorAll('input').forEach((input) => {
					input.disabled = false;
				});
			});
	});
}

/**
 * Product page: convert the on-screen quantity between "N cartons" and
 * "N pieces" client-side, and step +/- by whole units of whichever is active.
 *
 * @param {HTMLElement} toggle      The toggle container.
 * @param {number}      cartonSize  Units per carton.
 */
function initProductPageToggle(toggle, cartonSize) {
	const quantityField = toggle.closest('.quantity');
	const qtyInput = quantityField?.querySelector('input.qty');
	const hint = toggle.querySelector('[data-nitai-unit-hint]');

	if (!qtyInput || !hint) return;

	const originalMin = qtyInput.min || '1';
	const originalStep = qtyInput.step || '1';

	const updateHint = () => {
		const isCarton =
			toggle.querySelector('input[name="nitai_unit"]:checked')?.value === 'carton';

		if (!isCarton) {
			hint.hidden = true;
			return;
		}

		const cartons = Math.max(1, parseInt(qtyInput.value, 10) || 1);
		hint.hidden = false;
		hint.textContent = `= ${cartons * cartonSize} szt.`;
	};

	toggle.addEventListener('change', (event) => {
		if (event.target.name !== 'nitai_unit') return;

		const currentValue = Math.max(1, parseInt(qtyInput.value, 10) || 1);

		if (event.target.value === 'carton') {
			qtyInput.min = String(cartonSize);
			qtyInput.step = String(cartonSize);
			qtyInput.value = String(Math.max(1, Math.ceil(currentValue / cartonSize)));
		} else {
			qtyInput.min = originalMin;
			qtyInput.step = originalStep;
			qtyInput.value = String(Math.max(1, currentValue * cartonSize));
		}

		updateHint();
	});

	qtyInput.addEventListener('input', updateHint);
}
